import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { Mesh } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { GeometryProcessingService } from './geometry/geometry-processing.service';
import { ProjectConfigurationService } from './project-configuration.service';
import { ProgressService } from './progress.service';

export interface FrequencyEntry {
  index: number;
  value: number;
}

export type FreqResponseType = 'stress' | 'displacement' | 'acceleration';

export interface PerLcEntries {
  lc: number;
  caseId?: string;
  /**
   * Which quantity the FRF values represent — set by the runner based on the
   * optimizer's output file name (HE runs write Stress/Displacement/
   * Acceleration files depending on the constraint family). Absent on data
   * produced before optimizer 317859c — treat as 'stress'.
   */
  responseType?: FreqResponseType;
  values: FrequencyEntry[];
}

/**
 * Wrap legacy flat `[{index, value}]` JSON as a single-LC per-LC array so
 * downstream consumers can always assume the per-LC shape. New projects ship
 * the per-LC shape directly; old projects on S3 keep working unchanged.
 */
function normalizePerLc(raw: FrequencyEntry[] | PerLcEntries[] | undefined | null): PerLcEntries[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const first = raw[0] as FrequencyEntry | PerLcEntries;
  if (first && typeof first === 'object' && 'values' in first && Array.isArray((first as PerLcEntries).values)) {
    return raw as PerLcEntries[];
  }
  return [{ lc: 0, values: raw as FrequencyEntry[] }];
}

@Injectable({
  providedIn: 'root'
})
export class AssemblyLoaderService {
  private http = inject(HttpClient);
  private projectConfig = inject(ProjectConfigurationService);
  private progressService = inject(ProgressService);
  private geometryProcessing = inject(GeometryProcessingService);

  public loadingProject: boolean = true;
  public loadingProjectStatus: 'download' | 'init' = 'download';

  // Legacy progress properties - kept for backward compatibility
  public assemblyDownloadProgress: number = 0;
  public assemblyInitProgress = { total: 0, current: 0 };

  public parametricAssemblyAvailable = false;
  public organicAssemblyAvailable = false;
  public optimizedInfoAvailable = false;

  // Lazy loading state for optimized assembly info
  public loadCaseDataLoading = false;
  public loadCaseDataProgress = 0;
  public loadCaseDataMessage = '';

  // New version parallel loading
  public frequenciesResult?: any;
  // Flat shape kept as the default view (LC 0). Existing consumers continue to read this.
  public eigenFrequenciesResult?: { index: number; value: number }[];
  public freqResponseResult?: { index: number; value: number }[];
  // Per-LC shape for HE/RV multi-load-case projects. Falls back to a single-entry
  // wrapper for legacy flat-shape projects so old data on S3 keeps working.
  public eigenFrequenciesPerLc?: PerLcEntries[];
  public freqResponsePerLc?: PerLcEntries[];
  public manufacturingCostResult?: { machiningCost: number };
  public resultsAreVibrations = false;

  private loadedLoadCases = new Map<string, boolean>(); // Track which loadcases have been loaded
  /**
   * Per-loadcase in-flight requests — deduplicates concurrent callers for
   * the same key so rapid clicks (lc1 → lc2 → lc1) don't race each other's
   * progress state. Cleared alongside `loadedLoadCases` on every new
   * `getOptimizedAssemblyInfo` run (i.e. on every mf-case swap).
   */
  private loadCaseInflight = new Map<string, Promise<unknown>>();

  getAssembly(protoUrl: string): Promise<ArrayBuffer> {
    this.progressService.updateProgress(20, 'Downloading assembly...');

    const request = new HttpRequest('GET', protoUrl, { responseType: 'arraybuffer', reportProgress: true });

    return new Promise((resolve, reject) => {
      this.http.request<ArrayBuffer>(request).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            if (event.total) {
              const percentage = (100 / event.total) * event.loaded;
              this.assemblyDownloadProgress = parseFloat(percentage.toFixed(1));
              if (Number.isNaN(this.assemblyDownloadProgress)) this.assemblyDownloadProgress = 99;

              // Update progress within assembly loading phase
              // HTTP progress (0-100%) maps to sub-progress within assembly phase (15-50% overall)
              this.progressService.updateProgress(percentage, `Downloading assembly... ${Math.round(percentage)}%`);
            }
          }

          if (event.type === HttpEventType.Response) {
            this.loadingProjectStatus = 'init';
            this.progressService.updateProgress(100, 'Assembly downloaded');
            resolve(event.body as ArrayBuffer);
          }
        },
        error: (err) => reject(err)
      });
    });
  }

  async getOptimizedAssembly(): Promise<ArrayBuffer> {
    // Safe access: `optimization` may be undefined on brand-new projects or
    // on multi-mfg masters that haven't pre-merged a child yet. The
    // non-null assertion previously here threw instead of falling through
    // to the "No additive_design URL" branch below.
    const url = this.projectConfig.project.optimization?.additive_design;
    console.log('[AssemblyLoader] getOptimizedAssembly — URL:', url || '(empty)');
    if (!url) {
      console.warn('[AssemblyLoader] No additive_design URL — skipping organic assembly');
      throw new Error('No additive_design URL available');
    }
    this.progressService.updateTaskProgress('optimized-assembly', 0, 'Downloading optimized assembly...');

    const request = new HttpRequest('GET', url, {
      responseType: 'arraybuffer',
      reportProgress: true
    });

    return new Promise((resolve, reject) => {
      this.http.request<ArrayBuffer>(request).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const percentage = event.total && event.total > 0 ? (100 * event.loaded) / event.total : 0;
            this.assemblyDownloadProgress = parseFloat(percentage.toFixed(1));
            if (Number.isNaN(this.assemblyDownloadProgress)) this.assemblyDownloadProgress = 99;

            // Update task progress
            this.progressService.updateTaskProgress(
              'optimized-assembly',
              percentage,
              `Downloading optimized assembly... ${Math.round(percentage)}%`
            );
          } else if (event.type === HttpEventType.Response) {
            this.loadingProjectStatus = 'init';
            this.progressService.completeTask('optimized-assembly', 'Optimized assembly downloaded');
            resolve(event.body as ArrayBuffer);
          }
        },
        error: (err) => {
          console.error('Error downloading the assembly:', err);
          reject(err);
        }
      });
    });
  }

  async getOptimizedAssemblyParametric(): Promise<ArrayBuffer> {
    // Same defensive fix as `getOptimizedAssembly`: guard against
    // undefined `optimization` so the fallthrough-to-"no URL" branch
    // below runs instead of throwing on the `.additive_design_parametric`
    // access.
    const url = this.projectConfig.project.optimization?.additive_design_parametric;
    console.log('[AssemblyLoader] getOptimizedAssemblyParametric — URL:', url || '(empty)');
    if (!url) {
      console.warn('[AssemblyLoader] No additive_design_parametric URL — skipping parametric assembly');
      throw new Error('No additive_design_parametric URL available');
    }
    this.progressService.updateTaskProgress('parametric-assembly', 0, 'Downloading parametric assembly...');

    const request = new HttpRequest('GET', url, {
      responseType: 'arraybuffer',
      reportProgress: true
    });

    return new Promise((resolve, reject) => {
      this.http.request<ArrayBuffer>(request).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const percentage = event.total && event.total > 0 ? (100 * event.loaded) / event.total : 0;
            this.assemblyDownloadProgress = parseFloat(percentage.toFixed(1));
            if (Number.isNaN(this.assemblyDownloadProgress)) this.assemblyDownloadProgress = 99;

            // Update task progress
            this.progressService.updateTaskProgress(
              'parametric-assembly',
              percentage,
              `Downloading parametric assembly... ${Math.round(percentage)}%`
            );
          } else if (event.type === HttpEventType.Response) {
            this.loadingProjectStatus = 'init';
            this.progressService.completeTask('parametric-assembly', 'Parametric assembly downloaded');
            resolve(event.body as ArrayBuffer);
          }
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }

  // Helper to download with progress tracking
  downloadWithProgress<T>(
    url: string,
    progressTracker: { loaded: number; total: number; fileLoaded: Map<string, number>; fileTotal: Map<string, number> },
    onProgress: () => void,
    responseType: 'json' | 'arraybuffer' = 'json'
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const fileId = url;
      progressTracker.fileLoaded.set(fileId, 0);
      progressTracker.fileTotal.set(fileId, 0);

      this.http
        .get(url, {
          responseType: responseType as any,
          reportProgress: true,
          observe: 'events'
        })
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.DownloadProgress) {
              const prevLoaded = progressTracker.fileLoaded.get(fileId) || 0;
              const prevTotal = progressTracker.fileTotal.get(fileId) || 0;

              // Update file-specific progress
              progressTracker.fileLoaded.set(fileId, event.loaded);
              if (event.total) {
                progressTracker.fileTotal.set(fileId, event.total);
              }

              // Recalculate totals from all files
              let totalLoaded = 0;
              let totalSize = 0;
              for (const loaded of progressTracker.fileLoaded.values()) {
                totalLoaded += loaded;
              }
              for (const total of progressTracker.fileTotal.values()) {
                totalSize += total;
              }
              progressTracker.loaded = totalLoaded;
              progressTracker.total = totalSize;

              onProgress();
            } else if (event.type === HttpEventType.Response) {
              resolve(event.body as T);
            }
          },
          error: (err) => reject(err)
        });
    });
  }

  async getOptimizedAssemblyInfo(): Promise<any> {
    console.log('[AssemblyLoader] getOptimizedAssemblyInfo — flowVersion:', this.projectConfig.project.flowVersion);
    console.log('[AssemblyLoader] optimization object:', JSON.stringify(this.projectConfig.project.optimization)?.substring(0, 500));
    this.progressService.updateTaskProgress('optimization-info', 0, 'Loading optimization info...');

    // Reset lazy loading state. Any in-flight per-loadcase fetches from
    // the previous child are abandoned — they'll still resolve but the
    // engine has moved on, so their attribute writes land on now-orphaned
    // entries. The `loadCaseInflight` Map is wiped so new requests run
    // fresh against this child's URLs.
    this.loadedLoadCases.clear();
    this.loadCaseInflight.clear();
    this.geometryProcessing.loadCaseUrls = { organic: [], prismatic: [] };

    // Safe-guard: every v2 branch below uses `optimization!.v2!` non-null
    // assertions. If `optimization` (or `optimization.v2`) is missing, let
    // the caller's Promise.allSettled handle the absence cleanly rather
    // than the access chain throwing a confusing TypeError deep inside.
    if (!this.projectConfig.project.optimization) {
      throw new Error('No optimization data available');
    }

    if (this.projectConfig.project.flowVersion === 2) {
      console.log('[AssemblyLoader] v2 path — organicGeometryUrl:', this.projectConfig.project.optimization?.v2?.organic?.geometryUrl || '(empty)');
      console.log('[AssemblyLoader] v2 path — frequenciesExists:', this.projectConfig.project.optimization?.v2?.frequenciesExists);
      console.log('[AssemblyLoader] v2 path — organic loadcases count:', this.projectConfig.project.optimization?.v2?.organic?.loadcases?.length);
      // Vibrations
      if (this.projectConfig.project.optimization!.v2!.frequenciesExists) {
        this.progressService.updateTaskProgress('optimization-info', 10, 'Loading frequencies data...');
        const frequenciesResponse = await lastValueFrom(
          this.http.get(this.projectConfig.project.optimization!.v2!.frequencies)
        );
        this.frequenciesResult = frequenciesResponse;
        this.progressService.updateTaskProgress('optimization-info', 15, 'Frequencies data loaded');
      }

      // Eigenfrequencies (HE/RV) — accept either legacy flat shape or new per-LC shape.
      // Legacy: [{index, value}, ...]
      // New:    [{lc: 0, caseId?, values: [...]}, {lc: 1, ...}, ...]
      if (this.projectConfig.project.optimization!.v2!.eigenFrequenciesExists) {
        const eigenResponse = await lastValueFrom(
          this.http.get<FrequencyEntry[] | PerLcEntries[]>(this.projectConfig.project.optimization!.v2!.eigenFrequencies!)
        );
        this.eigenFrequenciesPerLc = normalizePerLc(eigenResponse);
        this.eigenFrequenciesResult = this.eigenFrequenciesPerLc[0]?.values ?? [];
      }

      // Frequency response modal stress (HE/RV) — same dual-shape handling.
      if (this.projectConfig.project.optimization!.v2!.freqResponseExists) {
        const frfResponse = await lastValueFrom(
          this.http.get<FrequencyEntry[] | PerLcEntries[]>(this.projectConfig.project.optimization!.v2!.freqResponseModalStress!)
        );
        this.freqResponsePerLc = normalizePerLc(frfResponse);
        this.freqResponseResult = this.freqResponsePerLc[0]?.values ?? [];
      }

      // Manufacturing cost (Milling2 only)
      if (this.projectConfig.project.optimization!.v2!.manufacturingCostExists) {
        const response = await lastValueFrom(
          this.http.get<{ machiningCost: number }>(
            this.projectConfig.project.optimization!.v2!.manufacturingCost!
          )
        );
        this.manufacturingCostResult = response;
      }

      // Load only the geometry, defer attribute loading
      const organicGenerator = async () => {
        this.progressService.updateTaskProgress('optimization-info', 20, 'Loading organic geometry...');
        const plyData = await lastValueFrom(
          this.http.get(this.projectConfig.project.optimization!.v2!.organic.geometryUrl, {
            responseType: 'arraybuffer'
          })
        );
        const plyGeometry = await this.geometryProcessing.processPLYGeometry(plyData, 20);
        const plyMesh = new Mesh(plyGeometry);
        this.geometryProcessing.organicMesh = plyMesh; // Cache for later use
        this.progressService.updateTaskProgress('optimization-info', 40, 'Organic geometry processed');

        const result: {
          id: string;
          t: Mesh | null;
          x: Mesh | null;
          y: Mesh | null;
          z: Mesh | null;
          s: Mesh | null;
          attributesLoaded?: boolean;
        }[] = [];

        if (this.projectConfig.project.optimization!.v2!.frequenciesExists === true) {
          this.resultsAreVibrations = true;
          // Store URLs for lazy loading
          for (let i = 0; i < 4; i++) {
            const loadcase = this.projectConfig.project.optimization!.v2!.organic.loadcases[i];
            const id = `${i + 1}`;
            this.geometryProcessing.loadCaseUrls.organic.push({
              id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t
            });
            result.push({ id, t: plyMesh, x: plyMesh, y: plyMesh, z: plyMesh, s: plyMesh, attributesLoaded: false });
          }
        } else {
          // Store URLs for lazy loading
          for (
            let i = 0;
            i < this.projectConfig.project.optimization!.v2!.organic.loadcases.length;
            i++
          ) {
            const loadcase = this.projectConfig.project.optimization!.v2!.organic.loadcases[i];
            this.geometryProcessing.loadCaseUrls.organic.push({
              id: loadcase.id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t,
              s: loadcase.s
            });
            result.push({
              id: loadcase.id,
              t: plyMesh,
              x: plyMesh,
              y: plyMesh,
              z: plyMesh,
              s: plyMesh,
              attributesLoaded: false
            });
          }
        }
        this.progressService.updateTaskProgress('optimization-info', 50, 'Organic loadcase info prepared');
        return result;
      };

      // Prismatic
      const prismaticGenerator = async () => {
        if (!this.projectConfig.project.optimization!.v2!.prismatic) return null;
        this.progressService.updateTaskProgress('optimization-info', 55, 'Loading prismatic geometry...');

        // Download PLY data
        const plyData = await lastValueFrom(
          this.http.get(this.projectConfig.project.optimization!.v2!.prismatic.geometryUrl, {
            responseType: 'arraybuffer'
          })
        );
        // Process in worker (or fallback to main thread)
        const plyGeometry = await this.geometryProcessing.processPLYGeometry(plyData, 20);
        const plyMesh = new Mesh(plyGeometry);
        this.geometryProcessing.prismaticMesh = plyMesh; // Cache for later use
        this.progressService.updateTaskProgress('optimization-info', 75, 'Prismatic geometry processed');

        const result: {
          id: string;
          t: Mesh | null;
          x: Mesh | null;
          y: Mesh | null;
          z: Mesh | null;
          s: Mesh | null;
          attributesLoaded?: boolean;
        }[] = [];

        if (this.projectConfig.project.optimization!.v2!.frequenciesExists) {
          this.resultsAreVibrations = true;
          // Store URLs for lazy loading
          for (let i = 0; i < 4; i++) {
            const loadcase = this.projectConfig.project.optimization!.v2!.prismatic.loadcases[i];
            const id = `${i + 1}`;
            this.geometryProcessing.loadCaseUrls.prismatic.push({
              id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t
            });
            result.push({
              id,
              t: plyMesh,
              x: plyMesh,
              y: plyMesh,
              z: plyMesh,
              s: plyMesh,
              attributesLoaded: false
            });
          }
        } else {
          // Store URLs for lazy loading
          for (
            let i = 0;
            i < this.projectConfig.project.optimization!.v2!.prismatic.loadcases.length;
            i++
          ) {
            const loadcase = this.projectConfig.project.optimization!.v2!.prismatic.loadcases[i];
            this.geometryProcessing.loadCaseUrls.prismatic.push({
              id: loadcase.id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t,
              s: loadcase.s
            });
            result.push({
              id: loadcase.id,
              t: plyMesh,
              x: plyMesh,
              y: plyMesh,
              z: plyMesh,
              s: plyMesh,
              attributesLoaded: false
            });
          }
        }
        this.progressService.updateTaskProgress('optimization-info', 90, 'Prismatic loadcase info prepared');
        return result;
      };

      const [organic, prismatic] = await Promise.all([organicGenerator(), prismaticGenerator()]);
      this.progressService.completeTask('optimization-info', 'Optimization info loaded');
      return { organic, prismatic };
    } else {
      // Legacy flow - keep the original behavior for now
      return this.getOptimizedAssemblyInfoLegacy();
    }
  }

  /**
   * Legacy loading method for flowVersion !== 2
   */
  private async getOptimizedAssemblyInfoLegacy(): Promise<any> {
    this.progressService.updateTaskProgress('optimization-info', 20, 'Loading legacy optimization info...');

    const progressTracker = {
      loaded: 0,
      total: 0,
      fileLoaded: new Map<string, number>(),
      fileTotal: new Map<string, number>()
    };

    const updateProgressMessage = (baseProgress: number, progressRange: number, label: string) => {
      const downloadProgress =
        progressTracker.total > 0 ? (progressTracker.loaded / progressTracker.total) * progressRange : 0;
      const progress = baseProgress + downloadProgress;
      const message = `${label}: ${this.geometryProcessing.formatBytes(progressTracker.loaded)} / ${this.geometryProcessing.formatBytes(progressTracker.total)}`;
      this.progressService.updateTaskProgress('optimization-info', progress, message);
    };

    const loader = new GLTFLoader();
    const result: { id: string; t: Mesh | null; x: Mesh | null; y: Mesh | null; z: Mesh | null; s: Mesh | null }[] =
      [];

    const loadMesh = async (url: string) => {
      try {
        const gltf = await loader.loadAsync(url);
        (gltf.scene.children[0] as Mesh).geometry.computeVertexNormals();
        return gltf.scene.children[0] as Mesh;
      } catch (error) {
        console.error(`Error loading mesh from ${url}:`, error);
        return null;
      }
    };

    this.progressService.updateTaskProgress('optimization-info', 25, 'Downloading loadcase data...');

    // Use Promise.all to run all load cases concurrently
    const loadcasePromises = this.projectConfig.project.optimization!.loadcases.map(async (loadcase) => {
      try {
        const [tMesh, xMesh, yMesh, zMesh, sMesh] = await Promise.all([
          loadMesh(loadcase.t),
          loadMesh(loadcase.x),
          loadMesh(loadcase.y),
          loadMesh(loadcase.z),
          loadMesh(loadcase.s)
        ]);

        const newResult = { id: loadcase.id, t: tMesh, x: xMesh, y: yMesh, z: zMesh, s: sMesh };

        try {
          const attributes = await this.downloadWithProgress(
            loadcase.attributes,
            progressTracker,
            () => updateProgressMessage(25, 70, 'Downloading loadcase data'),
            'json'
          );
          Object.assign(newResult, { attributes, attributesLoaded: true });
        } catch (error) {
          console.error(`Error loading attributes for loadcase ${loadcase.id}:`, error);
        }

        return newResult;
      } catch (error) {
        console.error(`Error processing loadcase ${loadcase.id}:`, error);
        return null;
      }
    });

    const loadcaseResults = await Promise.all(loadcasePromises);
    loadcaseResults.forEach((loadcase) => {
      if (loadcase) result.push(loadcase);
    });

    this.progressService.completeTask('optimization-info', 'Legacy optimization info loaded');
    return result;
  }

  /**
   * Lazy load attributes for a specific loadcase
   * @param loadcaseId - The ID of the loadcase to load
   * @param type - 'organic' or 'prismatic'
   * @param engine - The engine instance for updating optimized info
   * @returns The loaded attributes data
   */
  async loadLoadCaseAttributes(
    loadcaseId: string,
    type: 'organic' | 'prismatic' = 'organic',
    progressCallback?: (progress: number) => void,
    engine?: any
  ): Promise<any> {
    const cacheKey = `${type}_${loadcaseId}`;

    // Already successfully loaded — reuse engine-side attributes.
    if (this.loadedLoadCases.has(cacheKey)) {
      progressCallback?.(100);
      return null;
    }

    // A concurrent call for the same key is already running. Await it so
    // the caller's guard on `loadCaseData.attributes` sees the resolved
    // state, and avoid double-fetching / racing the engine-side writes.
    const existing = this.loadCaseInflight.get(cacheKey);
    if (existing) {
      try {
        await existing;
      } catch {
        /* the owning call already surfaced the error; fall through */
      }
      progressCallback?.(100);
      return null;
    }

    const urls =
      type === 'organic'
        ? this.geometryProcessing.loadCaseUrls.organic
        : this.geometryProcessing.loadCaseUrls.prismatic;
    const loadcaseUrl = urls.find((lc) => lc.id === loadcaseId);

    if (!loadcaseUrl) {
      console.warn(`Loadcase ${loadcaseId} not found in ${type} URLs`);
      return null;
    }

    const promise = this._loadLoadCaseAttributesInternal(
      cacheKey,
      loadcaseId,
      type,
      loadcaseUrl,
      progressCallback,
      engine,
    );
    this.loadCaseInflight.set(cacheKey, promise);
    try {
      return await promise;
    } finally {
      this.loadCaseInflight.delete(cacheKey);
    }
  }

  private async _loadLoadCaseAttributesInternal(
    cacheKey: string,
    loadcaseId: string,
    type: 'organic' | 'prismatic',
    loadcaseUrl: { id: string; t: string; x: string; y: string; z: string; s?: string },
    progressCallback?: (progress: number) => void,
    engine?: any,
  ): Promise<any> {
    // Yield to microtask queue before setting loading state.
    // This ensures Angular's current CD cycle completes before the state changes,
    // preventing NG0100 when called during component initialization.
    await Promise.resolve();
    this.loadCaseDataLoading = true;
    this.loadCaseDataProgress = 0;
    this.loadCaseDataMessage = `Loading ${type} loadcase data...`;
    progressCallback?.(0);

    const progressTracker = {
      loaded: 0,
      total: 0,
      fileLoaded: new Map<string, number>(),
      fileTotal: new Map<string, number>()
    };

    let progressRafId: number | null = null;
    let latestPercentage = 0;

    const updateProgress = () => {
      latestPercentage =
        progressTracker.total > 0 ? Math.round((progressTracker.loaded / progressTracker.total) * 100) : 0;
      // Scale download progress to 0-85% range, processing is 85-100%
      progressCallback?.(Math.round(latestPercentage * 0.85));
      // Coalesce UI property updates via requestAnimationFrame to prevent NG0100
      // Multiple XHR progress events per frame only apply the latest value
      if (progressRafId === null) {
        progressRafId = requestAnimationFrame(() => {
          progressRafId = null;
          this.loadCaseDataProgress = latestPercentage;
          this.loadCaseDataMessage = `Loading loadcase data: ${this.geometryProcessing.formatBytes(progressTracker.loaded)} / ${this.geometryProcessing.formatBytes(progressTracker.total)}`;
        });
      }
    };

    try {
      let attributes: any;
      const tPromise = this.downloadWithProgress(loadcaseUrl.t, progressTracker, updateProgress, 'json');
      const safeFetch = async (url: string | undefined): Promise<any> => {
        if (!url) return null;
        try {
          return await this.downloadWithProgress(url, progressTracker, updateProgress, 'json');
        } catch {
          return null;
        }
      };
      const sPromise = !this.resultsAreVibrations && loadcaseUrl.s
        ? this.downloadWithProgress(loadcaseUrl.s, progressTracker, updateProgress, 'json')
        : Promise.resolve(null);

      const [t, x, y, z, s] = await Promise.all([
        tPromise,
        safeFetch(loadcaseUrl.x),
        safeFetch(loadcaseUrl.y),
        safeFetch(loadcaseUrl.z),
        sPromise,
      ]);

      // Processing phase: 85-95%
      progressCallback?.(90);

      attributes = {
        x: x ?? t,
        y: y ?? t,
        z: z ?? t,
        t,
      };
      if (s) {
        attributes.s = s;
      }

      // Processing phase: 95%
      progressCallback?.(95);

      // Update the attributes in the correct optimizeInfoFlowVersion2 array based on type
      if (engine) {
        const targetArray =
          type === 'organic'
            ? engine.optimizeInfoFlowVersion2.organic
            : engine.optimizeInfoFlowVersion2.prismatic;

        if (targetArray) {
          const info = targetArray.find((i: any) => i.id === loadcaseId) as any;
          if (info) {
            info.attributes = attributes;
            info.attributesLoaded = true;
          }
        }

        // Also update in current assemblyOptimizedInfo if it matches
        const currentInfo = engine.assemblyOptimizedInfo.find((i: any) => i.id === loadcaseId) as any;
        if (currentInfo) {
          currentInfo.attributes = attributes;
          currentInfo.attributesLoaded = true;
        }
      }

      // Only set the "loaded" flag AFTER the engine-side writes succeed.
      // If anything above throws (or the engine was torn down mid-fetch),
      // the key stays absent from `loadedLoadCases` so a retry actually
      // re-fetches instead of being short-circuited against empty attrs.
      this.loadedLoadCases.set(cacheKey, true);

      this.loadCaseDataLoading = false;
      this.loadCaseDataMessage = 'Loadcase data loaded';
      progressCallback?.(100);

      return attributes;
    } catch (error) {
      if (progressRafId !== null) {
        cancelAnimationFrame(progressRafId);
        progressRafId = null;
      }
      console.error(`Error loading attributes for loadcase ${loadcaseId}:`, error);
      this.loadCaseDataLoading = false;
      this.loadCaseDataMessage = 'Error loading loadcase data';
      throw error;
    }
  }

  /**
   * Check if a loadcase's attributes have been loaded
   */
  isLoadCaseLoaded(loadcaseId: string, type: 'organic' | 'prismatic' = 'organic'): boolean {
    const cacheKey = `${type}_${loadcaseId}`;
    return this.loadedLoadCases.has(cacheKey);
  }
}
